// Pure policy helpers for the Virtua-backed conversation list.

/**
 * A released reader re-enters tail-follow mode only at the physical end.
 * Virtua's chat example allows 1.5px because fractional device-pixel ratios
 * can keep the scroll metrics from summing to exactly zero.
 */
export const CONVERSATION_TAIL_REENTRY_THRESHOLD_PX = 1.5;

export interface ScrollPolicyInput {
  /** Current offset from the start of the loaded Virtua rows. */
  offset: number;
  /** Whether the viewport is following the latest message. */
  atBottom: boolean;
  /** Distance from the first loaded row at which pagination may start. */
  startReachedThreshold: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  prepending: boolean;
  /** True for a live SSE tail that is not serialized with prepend commits. */
  streaming: boolean;
}

/**
 * Whether the viewport is at the start of the currently loaded rows.
 *
 * This deliberately relies only on Virtua's offset. `scrollSize` is floored
 * at `viewportSize`, so it cannot distinguish the start from the end of a
 * short or heavily collapsed conversation.
 */
export function isConversationAtLoadedStart({
  offset,
  startReachedThreshold,
}: Pick<ScrollPolicyInput, "offset" | "startReachedThreshold">): boolean {
  return offset < startReachedThreshold;
}

export function shouldLoadOlderOnScroll({
  offset,
  atBottom,
  startReachedThreshold,
  hasMore,
  isLoadingMore,
  prepending,
  streaming,
}: ScrollPolicyInput): boolean {
  return (
    !atBottom
    && !streaming
    && isConversationAtLoadedStart({ offset, startReachedThreshold })
    && hasMore
    && !isLoadingMore
    && !prepending
  );
}

/**
 * Estimate how many older canonical messages remain outside the loaded page.
 *
 * The number is presentation-only now; it does not contribute any pixels to
 * Virtua's scroll range. `hasMore` remains authoritative when the total
 * snapshot is stale after tool-start/terminal reconciliation.
 */
export interface ConversationOlderMessagesRemainingInput {
  hasMore: boolean;
  totalMessages: number | null;
  loadedMessageCount: number;
}

export function getConversationOlderMessagesRemaining({
  hasMore,
  totalMessages,
  loadedMessageCount,
}: ConversationOlderMessagesRemainingInput): number | null {
  if (!hasMore) return 0;
  if (totalMessages === null) return null;
  return Math.max(totalMessages - loadedMessageCount, 1);
}

/**
 * Virtua's `shift` is valid only for a pure start mutation. A live SSE can
 * append or replace the tail outside the pagination queue, so it remains
 * blocked. Recovery-poll merges are serialized by the feed and may opt in with
 * `polling=true` once that transaction has reached this point.
 */
export function computeShift(
  prepending: boolean,
  status: string,
  polling = false,
): boolean {
  return (
    prepending
    && status !== "submitted"
    && (status !== "streaming" || polling)
  );
}
